import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Mail, Link2, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { agentOnboardingAPI } from '@/lib/api';

/**
 * Send an onboarding link so a new agent completes their own profile.
 * Admin provides Email + Upline (agency) + NPN. On success the agent
 * gets an email with a secure link; completing it creates the agent +
 * portal access automatically.
 */
export const SendOnboardingLinkDialog = ({ open, onOpenChange, agencies = [], onSent }) => {
  const [email, setEmail] = useState('');
  const [agencyId, setAgencyId] = useState('');
  const [npn, setNpn] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setEmail(''); setAgencyId(''); setNpn(''); setResult(null); setCopied(false);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSend = async () => {
    if (!email.trim() || !agencyId || !npn.trim()) {
      toast.error('Email, Upline (agency) and NPN are all required');
      return;
    }
    setBusy(true);
    try {
      const { data } = await agentOnboardingAPI.createLink({
        email: email.trim(), agency_id: agencyId, npn: npn.trim(),
      });
      setResult(data);
      toast.success(data.email_sent ? 'Onboarding link emailed to the agent' : 'Link created (email not sent)');
      if (onSent) onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create onboarding link');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.onboard_link) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(result.onboard_link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = result.onboard_link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy automatically — select and copy the link manually');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="send-onboarding-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Link2 size={18} /> Send Onboarding Link
          </DialogTitle>
          <DialogDescription>
            The agent gets a secure email link to complete their own profile (name, phone,
            SSN, DOB and an optional photo). Completing it creates the agent and their portal access.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="ob-email">Agent Email</Label>
              <Input
                id="ob-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="agent@example.com"
                data-testid="onboarding-email-input"
              />
            </div>
            <div>
              <Label>Upline (Agency)</Label>
              <Select value={agencyId} onValueChange={setAgencyId}>
                <SelectTrigger data-testid="onboarding-agency-select">
                  <SelectValue placeholder="Select the agency the agent belongs to" />
                </SelectTrigger>
                <SelectContent>
                  {agencies.map((a) => (
                    <SelectItem key={a.agency_id} value={a.agency_id}>
                      {a.agency_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ob-npn">NPN</Label>
              <Input
                id="ob-npn" value={npn}
                onChange={(e) => setNpn(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="National Producer Number"
                data-testid="onboarding-npn-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                We'll check this NPN isn't already in use.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2" data-testid="onboarding-link-result">
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
              <div>
                {result.email_sent
                  ? <>An onboarding link was emailed to <strong>{result.email}</strong>.</>
                  : <>Link created for <strong>{result.email}</strong>. Email delivery is disabled in this environment — share the link below manually.</>}
                <div className="text-xs text-emerald-700 mt-1">
                  Agency: {result.agency_name} · NPN: {result.npn} · Expires in 7 days
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Shareable link</Label>
              <div className="flex gap-2">
                <Input readOnly value={result.onboard_link} className="text-xs" data-testid="onboarding-link-value" />
                <Button type="button" variant="outline" size="icon" onClick={copyLink} data-testid="onboarding-link-copy">
                  {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={busy}>Cancel</Button>
              <Button onClick={handleSend} disabled={busy} data-testid="onboarding-send-btn">
                {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Mail size={16} className="mr-2" />}
                Send Link
              </Button>
            </>
          ) : (
            <Button onClick={() => handleClose(false)} data-testid="onboarding-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendOnboardingLinkDialog;
